import { DivIcon } from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import * as React from 'react';
import { IHistoryPoint } from '../../../pages/MapPage';
import moment from 'moment';
import { dateFormats } from '../../../../utils/dateFormats';
import { ICluster } from './index';
import { setIncidentsTypes } from 'consts/adas';
import { convertHexToRGBA } from 'utils/color';
import { formatCoord } from '../../../../utils/map';

interface IProps {
  children?: React.ReactNode;
  incident: any;
  fill: any
}

const EllipseMarker = ({children, fill, incident }: IProps) => {
  return (
    <ellipse
      opacity={incident?.incident_type ? "0.5" : "0.15"}
      rx="49.7305"
      ry="50.0759"
      transform="matrix(0.99479 0.101949 -0.100533 0.994934 50.5531 50.441)"
      fill={fill}
    >
      {children}
    </ellipse>
  );
};

const HexagonMarker = ({ children, incident }: Pick<IProps, 'children' | 'incident'>) => {
  return (
    <polygon
      points="50,5 87,25 87,75 50,95 13,75 13,25"
      fill={'#ef6401'}
      opacity={incident?.incident_type ? "0.5" : "0.15"}
    >
      {children}
    </polygon>
  );
};

export const transportSelectedMarkerIcon = (
  course: number,
  fill: string,
  selected = false,
  isVehicle: boolean,
  incident?: ICluster | null,
) => {
  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        width="50"
        height="50"
        viewBox="0 0 101 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: `rotate(${270 +
            course}deg) translateX(-50%) translateY(-50%)`,
          transformOrigin: '0% 0%',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        {
          isVehicle ? 
          <EllipseMarker fill={fill} incident={incident} >
            {selected && (
              <animate
                attributeName="opacity"
                values="0.15;0.5;0.15"
                dur="1s"
                repeatCount="indefinite"
              />
            )}
          </EllipseMarker>
          :
          <HexagonMarker incident={incident} >
            {selected && (
              <animate
                attributeName="opacity"
                values="0.15;0.5;0.15"
                dur="1s"
                repeatCount="indefinite"
              />
            )}
          </HexagonMarker>
        }
        {
          isVehicle
          ? 
          <ellipse
            rx="30.9055"
            ry="30.7865"
            transform="matrix(-0.0191775 0.999816 -0.999811 -0.0194503 51.117 49.9526)"
            fill={fill}
          />
          :
          <polygon
            points="50,20 78,35 78,65 50,80 22,65 22,35"
            fill={'#ef6401'}
          />
        }
        <path
          d="M 40, 40
            L 65.5, 50
            L 40, 60
            L 43.5, 50
            Z"
          fill="white"
        />
        <defs>
          <filter
            id="filter0_d"
            x="30.3304"
            y="33.047"
            width="41.5731"
            height="41.8112"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
  });
}

export const stopIcon = new DivIcon({
  html: ReactDOMServer.renderToString(
    <svg
      height="28"
      width="28"
      viewBox="0 0 40 40"
      style={{
        transform: 'translateX(-50%) translateY(-50%)',
        left: '50%',
        top: '50%',
        position: 'absolute',
      }}
    >
      <circle
        fill="#003a72"
        stroke="#FFF"
        strokeWidth="3"
        r="14"
        cx="15"
        cy="15"
      />
    </svg>
  ),
});

export const controllerTeam = new DivIcon({
  html: ReactDOMServer.renderToString(
    <svg width="28" height="28" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" 
        fill="#ff5722" stroke="#d84315" strokeWidth="5" />
        <path
          d="M 40, 40
            L 65.5, 50
            L 40, 60
            L 43.5, 50
            Z"
          fill="white"
        />
    </svg>
  ),
});

export const historicalRoutePoint = (point: IHistoryPoint) =>
  new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        height="22"
        width="22"
        viewBox="0 0 101 101"
        style={{
          left: '50%',
          top: '50%',
          position: 'absolute',
          transform: `rotate(${270 +
            point.cs!}deg) translateX(-50%) translateY(-50%)`,
          transformOrigin: '0% 0%',
        }}
      >
        <title>
          {`
  ${point.tm ? moment
            .utc(point.tm)
            .local()
            .format(dateFormats.readableDateTimeSeconds).split(' ').join('\n') : ''}
Скорость: ${point.speed ? point.speed : 0} км/ч
Координаты: ${point.lat && point.lon ? `${formatCoord(point.lat)}; ${formatCoord(point.lon)}` : ''}
  `}
        </title>
        <ellipse
          rx="44"
          ry="44"
          transform="matrix(-0.0191775 0.999816 -0.999811 -0.0194503 51.117 49.9526)"
          fill="#0300B2"
        />
        <path
          d="M 25, 25
L 85, 50
L 25, 75
L 47.5, 50
Z"
          fill="white"
        />
        <defs>
          <filter
            id="filter0_d"
            x="30.3304"
            y="33.047"
            width="41.5731"
            height="41.8112"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    ),
  });
